import { useState, useEffect, useRef } from "react";
import {
  X, Lock, ShieldCheck, Eye, Copy, Plus, Loader2, KeyRound, FileText,
  AlertTriangle, Trash2, ClipboardCheck, ScrollText,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePresentation } from "@/context/PresentationContext";
import { useRestrictedVault } from "@/context/RestrictedVaultContext";
import { toast } from "sonner";

const FIELD_LABELS = {
  ssn: "Social Security #", driver_license_number: "Driver License #", driver_license_state: "DL State",
  routing_number: "Bank Routing #", account_number: "Bank Account #", payment_auth_reference: "Payment Auth Ref",
};
const PURPOSES = [
  ["complete_carrier_application", "Complete carrier application"],
  ["correct_application_information", "Correct application information"],
  ["verify_submitted_application", "Verify submitted application"],
  ["authorized_servicing_request", "Authorized servicing request"],
  ["other", "Other — explanation required"],
];

export function RestrictedVault() {
  const { user } = useAuth();
  const { isDemo } = usePresentation();
  const { open, clientId, intakeId, tab: initialTab, closeVault } = useRestrictedVault();
  const [tab, setTab] = useState("vault");
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [manual, setManual] = useState({});
  const [reauth, setReauth] = useState(null); // {field, action}
  const [pwd, setPwd] = useState("");
  const [stepMfaCode, setStepMfaCode] = useState("");
  const [purpose, setPurpose] = useState("complete_carrier_application");
  const [purposeNote, setPurposeNote] = useState("");
  const [revealed, setRevealed] = useState({}); // field -> value (auto-clears)
  const [prep, setPrep] = useState(null);
  const [appForm, setAppForm] = useState({ carrier: "", product: "", submitted_at: "", reference_number: "", next_followup_date: "", notes: "" });
  const timers = useRef({});
  const isAdmin = ["admin", "ops_manager"].includes(user?.role);

  const loadRec = async () => {
    if (isDemo) {
      const { data } = await api.get(`/restricted-vault/demo/${clientId || "demo"}`);
      setRec(data); return;
    }
    if (!clientId) { setRec({ exists: false }); return; }
    try { const { data } = await api.get(`/restricted-vault/records/${clientId}`); setRec(data); }
    catch (e) { setRec({ exists: false, error: formatApiErrorDetail(e?.response?.data?.detail) }); }
  };
  const loadPrep = async () => {
    if (!intakeId || isDemo) return;
    try { const { data } = await api.get(`/restricted-vault/prepare/${intakeId}`); setPrep(data); }
    catch { setPrep(null); }
  };

  useEffect(() => {
    if (open) {
      setTab(initialTab || (intakeId ? "prepare" : "vault")); setRevealed({}); setAddOpen(false); setManual({}); setReauth(null);
      loadRec(); loadPrep();
    }
    return () => { Object.values(timers.current).forEach(clearTimeout); timers.current = {}; };
  }, [open]); // eslint-disable-line

  if (!open) return null;

  const startReauth = (field, action) => {
    if (isDemo) { toast.info("Presentation Mode — reveal/copy disabled for fictional data."); return; }
    setReauth({ field, action }); setPwd(""); setPurpose("complete_carrier_application"); setPurposeNote("");
  };

  const doReauthAction = async () => {
    if (!pwd) { toast.error("Enter your password"); return; }
    if (purpose === "other" && !purposeNote.trim()) { toast.error("Explanation required for 'Other'"); return; }
    setBusy(true);
    try {
      const { data: g } = await api.post("/restricted-vault/reauth", { client_id: clientId, password: pwd, mfa_code: stepMfaCode.trim() });
      const ep = reauth.action === "copy" ? "copy" : "reveal";
      const { data } = await api.post(`/restricted-vault/${ep}`, {
        client_id: clientId, field: reauth.field, grant_id: g.grant_id,
        purpose: purpose === "other" ? "other" : purpose,
      });
      if (reauth.action === "copy") {
        try { await navigator.clipboard?.writeText(data.value); } catch { /* clipboard may be blocked */ }
        toast.success(data.message || "Copied for authorized use");
        setRevealed((r) => ({ ...r })); // do not persist copied value
      } else {
        const f = reauth.field;
        setRevealed((r) => ({ ...r, [f]: data.value }));
        clearTimeout(timers.current[f]);
        timers.current[f] = setTimeout(() => setRevealed((r) => { const n = { ...r }; delete n[f]; return n; }), (data.remask_after_seconds || 30) * 1000);
        toast.success("Revealed — auto-remasks in 30s");
      }
      setReauth(null); setPwd(""); setStepMfaCode("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Authorization failed");
    } finally { setBusy(false); }
  };

  const saveManual = async () => {
    const fields = Object.fromEntries(Object.entries(manual).filter(([, v]) => v && String(v).trim()));
    if (!Object.keys(fields).length) { toast.error("Enter at least one value"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/restricted-vault/records", { client_id: clientId, fields, purpose: "complete_carrier_application" });
      setRec({ exists: true, ...data }); setAddOpen(false); setManual({});
      toast.success("Saved to Restricted Application Data Vault (encrypted)");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const captureFromIntake = async () => {
    if (!intakeId) { toast.info("No source intake linked."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/restricted-vault/stage-from-intake/${intakeId}`);
      if (!data.staged) { toast.info("No securely-capturable sensitive values found in the intake text."); return; }
      const approve = window.confirm(`Detected (masked): ${data.categories.map((c) => `${c.label} ${c.masked}`).join(", ")}.\n\nEncrypt and save these to the vault?`);
      if (!approve) return;
      const { data: saved } = await api.post("/restricted-vault/commit-staged", {
        staging_id: data.staging_id, client_id: clientId,
        approved_fields: data.categories.map((c) => c.field), purpose: "complete_carrier_application",
      });
      setRec({ exists: true, ...saved });
      toast.success("Captured and encrypted from intake — original staging deleted");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Capture failed"); }
    finally { setBusy(false); }
  };

  const delField = async (field) => {
    if (!window.confirm(`Delete ${FIELD_LABELS[field]} from the vault? This removes the encrypted value.`)) return;
    try { await api.delete(`/restricted-vault/records/${clientId}/field/${field}`); loadRec(); toast.success("Field deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  const recordApplication = async () => {
    setBusy(true);
    try {
      await api.post(`/restricted-vault/prepare/${intakeId}/record`, { ...appForm, client_id: clientId });
      toast.success("Application recorded"); loadPrep();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const fields = rec?.fields || {};

  return (
    <div className="fixed inset-0 z-[70]" data-testid="restricted-vault-modal">
      <div className="absolute inset-0 bg-[#0a1826]/75 backdrop-blur-sm" onClick={closeVault} />
      <div className="absolute inset-x-0 top-0 mx-auto flex h-full w-full max-w-2xl flex-col bg-[#0f2439] text-slate-100 shadow-2xl sm:inset-y-4 sm:h-auto sm:max-h-[calc(100vh-2rem)] sm:rounded-2xl">
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
          <ShieldCheck className="h-5 w-5 text-[#B28A3D]" />
          <div className="font-heading font-bold">Restricted Application Data Vault</div>
          {isDemo && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">FICTIONAL DEMO</span>}
          <div className="flex-1" />
          <button onClick={closeVault} className="text-slate-300 hover:text-white" data-testid="rv-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex gap-1 border-b border-white/10 px-4 pt-2 text-sm">
          <button onClick={() => setTab("vault")} className={`rounded-t-md px-3 py-2 ${tab === "vault" ? "bg-white/10 font-semibold text-white" : "text-slate-400"}`} data-testid="rv-tab-vault">Vault</button>
          {intakeId && <button onClick={() => setTab("prepare")} className={`rounded-t-md px-3 py-2 ${tab === "prepare" ? "bg-white/10 font-semibold text-white" : "text-slate-400"}`} data-testid="rv-tab-prepare">Prepare Application</button>}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {tab === "vault" && (
            <>
              <div className="flex items-start gap-2 rounded-lg border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-3 text-[12px] text-slate-300">
                <Lock className="mt-0.5 h-4 w-4 text-[#B28A3D]" />
                <span>Values are field-level encrypted at rest and shown masked. Reveal/Copy require password re-authentication and are audited. This vault is separate from carrier/tool logins. <strong>Sensitive values are never sent to AI.</strong></span>
              </div>

              {!clientId && !isDemo && (
                <div className="rounded-lg bg-white/5 p-4 text-sm text-slate-300">Link this intake to a client first to store restricted data.</div>
              )}

              {(rec?.exists || isDemo) && Object.keys(fields).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(fields).map(([f, v]) => (
                    <div key={f} className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`rv-field-${f}`}>
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] uppercase tracking-wide text-slate-400">{FIELD_LABELS[f] || f}</div>
                          <div className="font-mono text-sm" data-testid={`rv-value-${f}`}>{revealed[f] ? revealed[f] : v.masked}</div>
                        </div>
                        {!isDemo && (
                          <div className="flex gap-1">
                            <button onClick={() => startReauth(f, "reveal")} className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-[11px] hover:bg-white/10" data-testid={`rv-reveal-${f}`}><Eye className="h-3 w-3" /> Reveal</button>
                            <button onClick={() => startReauth(f, "copy")} className="flex items-center gap-1 rounded-md bg-white/5 px-2 py-1 text-[11px] hover:bg-white/10" data-testid={`rv-copy-${f}`}><Copy className="h-3 w-3" /> Copy</button>
                            {isAdmin && <button onClick={() => delField(f)} className="rounded-md bg-white/5 px-2 py-1 text-slate-400 hover:bg-white/10 hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isDemo && <div className="text-[11px] text-amber-300">Presentation Mode — fictional sample only. Reveal/Copy are disabled and no production data is decrypted.</div>}
                </div>
              ) : (
                !isDemo && clientId && <div className="rounded-lg bg-white/5 p-4 text-sm text-slate-300">No restricted data on file yet. Sensitive information still needs secure entry.</div>
              )}

              {!isDemo && clientId && (
                <div className="space-y-2 pt-1">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setAddOpen((v) => !v)} className="flex items-center gap-1 rounded-md bg-[#17324D] px-3 py-1.5 text-[12px] hover:bg-[#22405f]" data-testid="rv-add-toggle"><Plus className="h-3.5 w-3.5" /> Manual secure entry</button>
                    {intakeId && <button onClick={captureFromIntake} disabled={busy} className="flex items-center gap-1 rounded-md bg-[#17324D] px-3 py-1.5 text-[12px] hover:bg-[#22405f]" data-testid="rv-capture-intake"><FileText className="h-3.5 w-3.5" /> Capture from intake (secure)</button>}
                  </div>
                  {addOpen && (
                    <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-3" data-testid="rv-manual-form">
                      {["ssn", "driver_license_number", "driver_license_state", "routing_number", "account_number"].map((f) => (
                        <div key={f} className="flex items-center gap-2">
                          <label className="w-40 shrink-0 text-[12px] text-slate-300">{FIELD_LABELS[f]}</label>
                          <input value={manual[f] || ""} onChange={(e) => setManual((m) => ({ ...m, [f]: e.target.value }))}
                            className="flex-1 rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" data-testid={`rv-manual-${f}`} />
                        </div>
                      ))}
                      <button onClick={saveManual} disabled={busy} className="flex items-center gap-1 rounded-md bg-[#B28A3D] px-3 py-1.5 text-[12px] font-semibold text-[#17324D]" data-testid="rv-manual-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Lock className="h-3.5 w-3.5" />} Encrypt &amp; save</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {tab === "prepare" && (
            <div className="space-y-3" data-testid="rv-prepare">
              <div className="rounded-lg bg-white/5 p-3 text-[12px] text-slate-300">Complete the application in the carrier's external portal. Fortunate Operations never auto-submits.</div>
              {prep?.non_sensitive_fields?.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <div className="mb-1 text-sm font-semibold">Approved client fields</div>
                  <div className="grid grid-cols-2 gap-1 text-[12px]">
                    {prep.non_sensitive_fields.map((f, i) => <div key={i}><span className="text-slate-400">{f.label}:</span> {f.value}</div>)}
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="mb-1 text-sm font-semibold">Sensitive fields on file</div>
                {Object.keys(fields).length ? Object.entries(fields).map(([f, v]) => (
                  <div key={f} className="flex items-center justify-between py-0.5 text-[12px]">
                    <span>{FIELD_LABELS[f]}: <span className="font-mono">{revealed[f] || v.masked}</span></span>
                    <span className="flex gap-1">
                      <button onClick={() => startReauth(f, "reveal")} className="rounded bg-white/5 px-2 py-0.5 hover:bg-white/10"><Eye className="h-3 w-3" /></button>
                      <button onClick={() => startReauth(f, "copy")} className="rounded bg-white/5 px-2 py-0.5 hover:bg-white/10"><Copy className="h-3 w-3" /></button>
                    </span>
                  </div>
                )) : <div className="text-[12px] text-slate-400">None on file — use the Vault tab to add securely.</div>}
              </div>
              {prep?.missing_checklist?.length > 0 && (
                <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-[12px]">
                  <div className="flex items-center gap-1 font-semibold text-amber-200"><AlertTriangle className="h-3.5 w-3.5" /> Missing / needs review</div>
                  <div className="text-slate-300">{prep.missing_checklist.join(", ")}</div>
                </div>
              )}
              <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="text-sm font-semibold">Record application (after submitting in carrier portal)</div>
                {[["carrier", "Carrier"], ["product", "Product"], ["reference_number", "Application/Reference #"]].map(([k, l]) => (
                  <input key={k} placeholder={l} value={appForm[k]} onChange={(e) => setAppForm((a) => ({ ...a, [k]: e.target.value }))} className="w-full rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" data-testid={`rv-app-${k}`} />
                ))}
                <div className="flex gap-2">
                  <input type="date" value={appForm.submitted_at} onChange={(e) => setAppForm((a) => ({ ...a, submitted_at: e.target.value }))} className="flex-1 rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" title="Submitted date" />
                  <input type="date" value={appForm.next_followup_date} onChange={(e) => setAppForm((a) => ({ ...a, next_followup_date: e.target.value }))} className="flex-1 rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" title="Next follow-up" />
                </div>
                <textarea placeholder="Notes (no sensitive values)" value={appForm.notes} onChange={(e) => setAppForm((a) => ({ ...a, notes: e.target.value }))} rows={2} className="w-full rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" />
                <button onClick={recordApplication} disabled={busy} className="flex items-center gap-1 rounded-md bg-[#B28A3D] px-3 py-1.5 text-[12px] font-semibold text-[#17324D]" data-testid="rv-app-record"><ClipboardCheck className="h-3.5 w-3.5" /> Record application</button>
              </div>
            </div>
          )}
        </div>

        {/* Re-auth dialog */}
        {reauth && (
          <div className="absolute inset-0 z-10 grid place-items-center bg-[#0a1826]/80 p-4" data-testid="rv-reauth">
            <div className="w-full max-w-sm space-y-3 rounded-xl border border-white/10 bg-[#12293f] p-5">
              <div className="flex items-center gap-2 font-heading font-bold"><KeyRound className="h-4 w-4 text-[#B28A3D]" /> Confirm your password</div>
              <p className="text-[12px] text-slate-300">Re-authenticate to {reauth.action} <strong>{FIELD_LABELS[reauth.field]}</strong>. This is audited.</p>
              <input type="password" autoComplete="off" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Your account password" className="w-full rounded-md bg-white/95 px-3 py-2 text-sm text-slate-900" data-testid="rv-reauth-pwd" />
              <input inputMode="numeric" autoComplete="off" value={stepMfaCode} onChange={(e) => setStepMfaCode(e.target.value)} placeholder="Authenticator code (if MFA is enabled)" className="w-full rounded-md bg-white/95 px-3 py-2 text-sm text-slate-900" data-testid="rv-reauth-mfa" />
              <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className="w-full rounded-md bg-white/95 px-3 py-2 text-sm text-slate-900" data-testid="rv-reauth-purpose">
                {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {purpose === "other" && <input value={purposeNote} onChange={(e) => setPurposeNote(e.target.value)} placeholder="Explanation" className="w-full rounded-md bg-white/95 px-3 py-2 text-sm text-slate-900" />}
              <div className="flex gap-2">
                <button onClick={doReauthAction} disabled={busy} className="flex flex-1 items-center justify-center gap-1 rounded-md bg-[#B28A3D] px-3 py-2 text-sm font-semibold text-[#17324D]" data-testid="rv-reauth-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Confirm</button>
                <button onClick={() => setReauth(null)} className="rounded-md border border-white/15 px-3 py-2 text-sm">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
